import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Package, Clock, DollarSign, Upload, X, Calculator, Star, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { apiClient } from '@/services/api'

interface Project {
  id: string
  name: string
  type: string
  description?: string
  status: string
  customMultiplier: number
  targetSellingPrice?: number
  estimatedHours?: number
  customerName?: string
  completedAt: string
  imageUrl?: string
  projectMaterials: ProjectMaterial[]
  timeEntries: TimeEntry[]
}

interface ProjectMaterial {
  id: string
  materialId: string
  materialName: string
  quantityUsed: number
  unitType: string
  totalCost: number
}

interface TimeEntry {
  id: string
  activityType: string
  hours: number
  hourlyRate: number
  totalCost: number
}

interface ProductFormData {
  name: string
  description: string
  actualSellingPrice: string
  isFeatured: boolean
  isActive: boolean
  additionalNotes: string
}

export const CreateProductFromProjectPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId?: string }>()
  
  const [project, setProject] = useState<Project | null>(null)
  const [completedProjects, setCompletedProjects] = useState<Project[]>([])
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    actualSellingPrice: '',
    isFeatured: false,
    isActive: true,
    additionalNotes: ''
  })
  const [productImages, setProductImages] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (projectId) {
      fetchProject(projectId)
    } else {
      fetchCompletedProjects()
    }
  }, [projectId])

  const fetchProject = async (id: string) => {
    try {
      const projectData = await apiClient.get(`/projects/${id}`)
      
      // Transform the response to match our interface
      const transformedProject: Project = {
        id: projectData.id,
        name: projectData.name,
        type: projectData.type,
        description: projectData.description,
        status: projectData.status,
        customMultiplier: parseFloat(projectData.customMultiplier?.toString()) || 1.5,
        targetSellingPrice: parseFloat(projectData.targetSellingPrice?.toString()) || undefined,
        estimatedHours: parseFloat(projectData.estimatedHours?.toString()) || undefined,
        customerName: projectData.customerName,
        completedAt: projectData.completedAt || projectData.updatedAt,
        imageUrl: projectData.imageUrl,
        projectMaterials: projectData.projectMaterials?.map((pm: any) => ({
          id: pm.id,
          materialId: pm.materialId,
          materialName: pm.material?.name || pm.materialName,
          quantityUsed: parseFloat(pm.quantityUsed?.toString()) || 0,
          unitType: pm.material?.baseUnit || pm.material?.unitType || pm.unitType || 'unit',
          totalCost: parseFloat(pm.totalCost?.toString()) || 0
        })) || [],
        timeEntries: projectData.timeEntries?.map((te: any) => ({
          id: te.id,
          activityType: te.activityType,
          hours: parseFloat(te.hours?.toString()) || 0,
          hourlyRate: parseFloat(te.hourlyRate?.toString()) || 0,
          totalCost: parseFloat(te.totalCost?.toString()) || 0
        })) || []
      }
      
      setProject(transformedProject)
      setFormData(prev => ({
        ...prev,
        name: transformedProject.name,
        description: transformedProject.description || '',
        actualSellingPrice: transformedProject.targetSellingPrice?.toString() || ''
      }))
    } catch (error) {
      console.error('Error fetching project:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCompletedProjects = async () => {
    try {
      const data = await apiClient.get('/projects?status=completed')
      const projects = Array.isArray(data) ? data : data.projects || []
      
      // Transform projects to match our interface
      const transformedProjects: Project[] = projects.map((project: any) => ({
        id: project.id,
        name: project.name,
        type: project.type,
        description: project.description,
        status: project.status,
        customMultiplier: parseFloat(project.customMultiplier?.toString()) || 1.5,
        targetSellingPrice: parseFloat(project.targetSellingPrice?.toString()) || undefined,
        estimatedHours: parseFloat(project.estimatedHours?.toString()) || undefined,
        customerName: project.customerName,
        completedAt: project.completedAt || project.updatedAt,
        imageUrl: project.imageUrl,
        projectMaterials: project.projectMaterials || [],
        timeEntries: project.timeEntries || []
      }))
      
      setCompletedProjects(transformedProjects)
    } catch (error) {
      console.error('Error fetching completed projects:', error)
      setCompletedProjects([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof ProductFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleImageUpload = (files: FileList | null) => {
    if (!files) return
    
    const newFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        alert('Please select only image files')
        return false
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Image size should be less than 5MB')
        return false
      }
      return true
    })

    setProductImages(prev => [...prev, ...newFiles])
  }

  const removeImage = (index: number) => {
    setProductImages(prev => prev.filter((_, i) => i !== index))
  }

  const calculateCosts = () => {
    if (!project) return { materialCost: 0, laborCost: 0, totalCost: 0, totalHours: 0 }

    const materialCost = project.projectMaterials.reduce((sum, material) => sum + material.totalCost, 0)
    
    // Calculate labor cost from time entries if available
    let laborCost = project.timeEntries.reduce((sum, entry) => sum + entry.totalCost, 0)
    let totalHours = project.timeEntries.reduce((sum, entry) => sum + entry.hours, 0)
    
    // If no time entries but project has estimated hours, calculate labor cost using default rate
    if (laborCost === 0 && totalHours === 0) {
      // Check if project has estimatedHours field
      const estimatedHours = project.estimatedHours ? parseFloat(project.estimatedHours.toString()) : 0
      if (estimatedHours > 0) {
        const defaultHourlyRate = 25.00 // Default hourly rate
        laborCost = estimatedHours * defaultHourlyRate
        totalHours = estimatedHours
      }
    }
    
    const totalCost = materialCost + laborCost

    return { materialCost, laborCost, totalCost, totalHours }
  }

  const calculateSuggestedPrice = () => {
    if (!project) return 0
    const { totalCost } = calculateCosts()
    return totalCost * project.customMultiplier
  }

  const calculateProfitMargin = () => {
    const actualPrice = parseFloat(formData.actualSellingPrice) || 0
    const { totalCost } = calculateCosts()
    if (actualPrice <= 0) return 0
    return ((actualPrice - totalCost) / actualPrice) * 100
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) newErrors.name = 'Product name is required'
    if (formData.actualSellingPrice && parseFloat(formData.actualSellingPrice) <= 0) {
      newErrors.actualSellingPrice = 'Selling price must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || !project) return

    setIsSubmitting(true)
    try {
      // Create FormData to handle both JSON data and file uploads
      const formDataToSend = new FormData()
      
      // Add product data as JSON
      const productData = {
        projectId: project.id,
        name: formData.name,
        description: formData.description,
        actualSellingPrice: formData.actualSellingPrice ? parseFloat(formData.actualSellingPrice) : undefined,
        isFeatured: formData.isFeatured,
        isActive: formData.isActive,
        notes: formData.additionalNotes,
        additionalImages: [] // Will be handled by file uploads
      }

      // Add each field individually to FormData
      Object.entries(productData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formDataToSend.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
        }
      })

      // Add image files
      productImages.forEach((file, index) => {
        formDataToSend.append('images', file)
      })

      // Send as multipart/form-data
      const result = await fetch('/api/v1/products/from-project', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formDataToSend
      })

      if (!result.ok) {
        const errorData = await result.text()
        throw new Error(`HTTP ${result.status}: ${errorData}`)
      }

      const responseData = await result.json()
      console.log('Product created successfully:', responseData)
      
      navigate('/products')
    } catch (error) {
      console.error('Error creating product:', error)
      alert('Error creating product. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-600 dark:text-gray-400">Loading project...</p>
        </div>
      </div>
    )
  }

  if (!projectId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/products')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Products
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Create Product from Project
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Select a completed project to convert into a product
            </p>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
              <p className="text-gray-600 dark:text-gray-400">Loading completed projects...</p>
            </div>
          </div>
        ) : completedProjects.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Completed Projects Found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You need to have completed projects to create products from them.
              </p>
              <Button onClick={() => navigate('/projects')}>
                Go to Projects
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {completedProjects.map((project) => (
              <Card key={project.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/products/create-from-project/${project.id}`)}>
                <div className="aspect-w-16 aspect-h-9 bg-gray-100 dark:bg-gray-800 relative">
                  {project.imageUrl ? (
                    <img
                      src={project.imageUrl}
                      alt={project.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center">
                      <Package className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3" />
                    <span className="text-xs font-medium">Completed</span>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {project.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                    {project.description}
                  </p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Type:</span>
                      <span className="font-medium capitalize">{project.type}</span>
                    </div>
                    {project.customerName && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Customer:</span>
                        <span className="font-medium">{project.customerName}</span>
                      </div>
                    )}
                    {project.targetSellingPrice && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Target Price:</span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(project.targetSellingPrice)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Completed:</span>
                      <span className="font-medium">{formatDate(project.completedAt)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (!project) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/products')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Products
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Project Not Found
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              The selected project could not be found or is not completed.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const costs = calculateCosts()
  const suggestedPrice = calculateSuggestedPrice()
  const profitMargin = calculateProfitMargin()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/products')}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Back to Products
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Create Product from Project
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Convert your completed project into a reusable product template
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Project Information */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Package className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Project Information
            </h2>
            <Badge variant="success" size="sm">
              {project.status}
            </Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-4">Project Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Project Name:</span>
                  <span className="font-medium">{project.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Type:</span>
                  <span className="font-medium capitalize">{project.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Customer:</span>
                  <span className="font-medium">{project.customerName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Completed:</span>
                  <span className="font-medium">{formatDate(project.completedAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Multiplier:</span>
                  <span className="font-medium">{project.customMultiplier}x</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-4">Cost Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Material Cost:</span>
                  <span className="font-medium">{formatCurrency(costs.materialCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Labor Cost:</span>
                  <span className="font-medium">{formatCurrency(costs.laborCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total Hours:</span>
                  <span className="font-medium">{costs.totalHours}h</span>
                </div>
                <div className="flex justify-between border-t pt-3 font-semibold">
                  <span>Total Cost:</span>
                  <span>{formatCurrency(costs.totalCost)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Suggested Price:</span>
                  <span className="font-semibold">{formatCurrency(suggestedPrice)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Materials Used */}
          <div className="mt-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">Materials Used</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2">Material</th>
                    <th className="text-right py-2">Quantity</th>
                    <th className="text-right py-2">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {project.projectMaterials.map((material) => (
                    <tr key={material.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2">{material.materialName}</td>
                      <td className="text-right py-2">
                        {material.quantityUsed} {material.unitType}
                      </td>
                      <td className="text-right py-2">{formatCurrency(material.totalCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Time Entries */}
          <div className="mt-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">Time Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2">Activity</th>
                    <th className="text-right py-2">Hours</th>
                    <th className="text-right py-2">Rate</th>
                    <th className="text-right py-2">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {project.timeEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 capitalize">{entry.activityType.replace('_', ' ')}</td>
                      <td className="text-right py-2">{entry.hours}h</td>
                      <td className="text-right py-2">{formatCurrency(entry.hourlyRate)}/h</td>
                      <td className="text-right py-2">{formatCurrency(entry.totalCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {/* Product Information */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Star className="w-5 h-5 text-yellow-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Product Information
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  handleInputChange('name', e.target.value)
                }
                placeholder="Product name"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Actual Selling Price (Optional)
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.actualSellingPrice}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  handleInputChange('actualSellingPrice', e.target.value)
                }
                placeholder={suggestedPrice.toFixed(2)}
                className={errors.actualSellingPrice ? 'border-red-500' : ''}
              />
              {errors.actualSellingPrice && (
                <p className="text-red-500 text-sm mt-1">{errors.actualSellingPrice}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Suggested: {formatCurrency(suggestedPrice)}
              </p>
            </div>
          </div>

          {formData.actualSellingPrice && parseFloat(formData.actualSellingPrice) > 0 && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-green-900 dark:text-green-100">
                  Profit Margin:
                </span>
                <span className="text-xl font-bold text-green-900 dark:text-green-100">
                  {profitMargin.toFixed(1)}%
                </span>
              </div>
              <div className="text-sm text-green-700 dark:text-green-300 mt-1">
                Profit: {formatCurrency(parseFloat(formData.actualSellingPrice) - costs.totalCost)}
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Product Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                handleInputChange('description', e.target.value)
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Describe this product for your catalog..."
            />
          </div>

          <div className="mt-6 flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isFeatured}
                onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Featured Product
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active Product
              </span>
            </label>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData.additionalNotes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                handleInputChange('additionalNotes', e.target.value)
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Any additional notes about this product..."
            />
          </div>

          {/* Product Images */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Product Images (Optional)
            </label>
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e.target.files)}
              />
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">
                      Click to upload product images
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    PNG, JPG, GIF up to 5MB each
                  </p>
                </div>
              </div>
            </label>
            
            {productImages.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {productImages.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Product ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/products')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            leftIcon={isSubmitting ? undefined : <Package className="w-4 h-4" />}
          >
            {isSubmitting ? 'Creating Product...' : 'Create Product'}
          </Button>
        </div>
      </form>
    </div>
  )
}